import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE = 'https://www.remonty-gorzow.pl';
const OG_IMAGE = `${BASE}/og-image.jpg`;

type Meta = { title: string; description: string };

// Meta dla konkretnych tras (dopasowanie dokładne)
const META: Record<string, Meta> = {
  '/': {
    title: 'Karlito — Remonty i wykończenia wnętrz Gorzów Wielkopolski',
    description:
      'Kompleksowe remonty mieszkań i łazienek w Gorzowie Wlkp. 20 lat doświadczenia, stała ekipa, umowa i gwarancja. Darmowa wycena — tel. 791 308 016.',
  },
  '/uslugi': {
    title: 'Usługi remontowe — mieszkania, łazienki, pod klucz | Karlito',
    description:
      'Zakres usług Karlito: remonty mieszkań, wykończenia pod klucz, remonty łazienek i kuchni, malowanie, panele. Gorzów Wielkopolski i okolice.',
  },
  '/remonty-gorzow': {
    title: 'Remonty Gorzów Wielkopolski — mieszkania i łazienki | Karlito',
    description:
      'Profesjonalne remonty mieszkań, łazienek i wykończenia wnętrz w Gorzowie Wielkopolskim. Stała ekipa, terminowość, gwarancja. Darmowa wycena.',
  },
  '/realizacje': {
    title: 'Realizacje — nasze remonty w Gorzowie | Karlito',
    description:
      'Zobacz realizacje remontów mieszkań i łazienek w Gorzowie Wlkp. Zdjęcia efektów, zakres prac i inspiracje od ekipy Karlito.',
  },
  '/jak-pracujemy': {
    title: 'Jak pracujemy — proces remontu krok po kroku | Karlito',
    description:
      'Od konsultacji po odbiór: wycena, umowa, realizacja i gwarancja. Poznaj przejrzysty proces remontu w Karlito, Gorzów Wielkopolski.',
  },
  '/cennik': {
    title: 'Cennik remontów — orientacyjne ceny | Karlito Gorzów',
    description:
      'Orientacyjny cennik remontów i wykończeń wnętrz w Gorzowie Wlkp. Sprawdź zakres prac i zamów bezpłatną, indywidualną wycenę.',
  },
  '/o-nas': {
    title: 'O nas — Karlito, remonty w Gorzowie Wielkopolskim',
    description:
      'Karlito to stała ekipa z 20-letnim doświadczeniem w remontach i wykończeniach wnętrz w Gorzowie Wlkp. Solidnie, terminowo, z gwarancją.',
  },
  '/faq': {
    title: 'FAQ — najczęstsze pytania o remonty | Karlito',
    description:
      'Odpowiedzi na najczęstsze pytania o remonty i wykończenia wnętrz: wycena, terminy, umowa, gwarancja. Karlito, Gorzów Wielkopolski.',
  },
  '/kontakt': {
    title: 'Kontakt — Karlito remonty Gorzów, tel. 791 308 016',
    description:
      'Skontaktuj się z Karlito: tel. 791 308 016, kontakt@remonty-gorzow.pl. Darmowa wycena remontu w Gorzowie Wielkopolskim i okolicach.',
  },
  '/blog': {
    title: 'Blog remontowy — porady i inspiracje | Karlito',
    description:
      'Porady remontowe, inspiracje i praktyczne wskazówki od ekipy Karlito. Remonty mieszkań, łazienek i wykończenia wnętrz w Gorzowie Wlkp.',
  },
  '/wykonczenia-wnetrz-gorzow': {
    title: 'Wykończenia wnętrz Gorzów Wielkopolski | Karlito',
    description:
      'Kompleksowe wykończenia wnętrz pod klucz w Gorzowie Wlkp. Gładzie, malowanie, płytki, panele, zabudowy GK. Stała ekipa i gwarancja.',
  },
  '/remont-lazienki-gorzow': {
    title: 'Remont łazienki Gorzów Wielkopolski | Karlito',
    description:
      'Remonty łazienek w Gorzowie Wlkp od A do Z: hydraulika, płytki, biały montaż, wykończenie. Solidnie, terminowo, z gwarancją.',
  },
  '/malowanie-gorzow': {
    title: 'Malowanie i gładzie Gorzów Wielkopolski | Karlito',
    description:
      'Malowanie ścian, gładzie gipsowe i wykończenia w Gorzowie Wlkp. Czysto, dokładnie i terminowo. Darmowa wycena — Karlito.',
  },
  '/podlogi-gorzow': {
    title: 'Układanie podłóg i paneli Gorzów Wielkopolski | Karlito',
    description:
      'Montaż paneli, podłóg i listew w Gorzowie Wlkp. Profesjonalnie i terminowo, ze stałą ekipą. Zamów bezpłatną wycenę w Karlito.',
  },
};

const DEFAULT_META: Meta = META['/'];

function prettifySlug(slug: string): string {
  const s = decodeURIComponent(slug).replace(/-/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function resolveMeta(pathname: string): Meta {
  const path = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  if (META[path]) return META[path];

  const parts = path.split('/').filter(Boolean);
  if (parts.length === 2) {
    const [section, slug] = parts;
    const name = prettifySlug(slug);
    if (section === 'blog') {
      return {
        title: `${name} — Blog | Karlito`,
        description: `${name} — porady i inspiracje remontowe od ekipy Karlito, Gorzów Wielkopolski.`,
      };
    }
    if (section === 'uslugi') {
      return {
        title: `${name} — usługi remontowe | Karlito Gorzów`,
        description: `${name} w Gorzowie Wielkopolskim. Stała ekipa, terminowość i gwarancja. Zamów bezpłatną wycenę w Karlito.`,
      };
    }
    if (section === 'realizacje') {
      return {
        title: `Realizacja — ${name} | Karlito Gorzów`,
        description: `Realizacja Karlito: ${name}. Zobacz efekty naszych remontów w Gorzowie Wielkopolskim.`,
      };
    }
  }
  return DEFAULT_META;
}

function upsertMetaByName(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertMetaByProp(property: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = resolveMeta(pathname);
    const url = `${BASE}${pathname === '/' ? '/' : pathname.replace(/\/$/, '')}`;

    document.title = meta.title;
    upsertMetaByName('description', meta.description);
    upsertCanonical(url);

    upsertMetaByProp('og:title', meta.title);
    upsertMetaByProp('og:description', meta.description);
    upsertMetaByProp('og:url', url);
    upsertMetaByProp('og:image', OG_IMAGE);

    upsertMetaByName('twitter:title', meta.title);
    upsertMetaByName('twitter:description', meta.description);
    upsertMetaByName('twitter:image', OG_IMAGE);
  }, [pathname]);

  return null;
}
